import { MARKET, MARKET_EVENTS, TRADE_GOODS, TRADE_GOOD_IDS, type TradeGoodId } from '@sd/content';

/**
 * The system exchange (GDD §6), Offworld-style: infinite liquidity, but
 * every unit bought pushes the price up by (1 + impact) and every unit sold
 * pushes it down by the same factor; between trades the price drifts back
 * to base. Buying q units and selling them straight back returns the price
 * to where it was and always loses money, so the market has no free loop.
 *
 * All prices are in ГС (galactic standard). Pure functions only.
 */

export interface MarketGood {
  /** Current price, ГС per unit. */
  readonly price: number;
  /** Units players sold minus units they bought since the start (for oversupply events later). */
  readonly netSold: number;
  /** Units traded in the current economic cycle. */
  readonly cycleVolume: number;
  /** The price does not drift back before this tick (dumping, GDD §6). */
  readonly holdUntil?: number;
  /** `netSold` at the start of the current cycle. */
  readonly netAtCycleStart?: number;
  /** Net units sold in each recent cycle, oldest first (glut and shortage events). */
  readonly netHistory?: readonly number[];
}

export type MarketState = Readonly<Record<TradeGoodId, MarketGood>>;

export type TradeSide = 'buy' | 'sell';

export interface Quote {
  readonly side: TradeSide;
  readonly good: TradeGoodId;
  readonly quantity: number;
  /** Value of the goods at market prices, before the fee. */
  readonly gross: number;
  readonly fee: number;
  /** Buy: what the player pays. Sell: what the player receives. */
  readonly total: number;
  readonly priceAfter: number;
}

/** Money is kept in hundredths so states stay readable and comparable. */
export const roundMoney = (value: number): number => Math.round(value * 100) / 100;

export function createMarket(): MarketState {
  return Object.fromEntries(
    TRADE_GOOD_IDS.map((id) => [
      id,
      { price: TRADE_GOODS[id].basePrice, netSold: 0, cycleVolume: 0 },
    ]),
  ) as Record<TradeGoodId, MarketGood>;
}

export function priceBounds(good: TradeGoodId): { min: number; max: number } {
  const base = TRADE_GOODS[good].basePrice;
  return { min: base * MARKET.minPriceFactor, max: base * MARKET.maxPriceFactor };
}

export function isTradeGood(value: string): value is TradeGoodId {
  return (TRADE_GOOD_IDS as readonly string[]).includes(value);
}

/**
 * Prices of `quantity` consecutive units: the first at `first`, each next one
 * multiplied by `step` (> 1 when buying, < 1 when selling), never crossing
 * `limit` — units past it trade at the limit.
 */
function walk(first: number, step: number, quantity: number, limit: number) {
  const rising = step > 1;
  const beyond = rising ? first >= limit : first <= limit;
  // Units whose price stays on our side of the limit: indices 0..free-1.
  const free = beyond
    ? 0
    : Math.min(quantity, Math.floor(Math.log(limit / first) / Math.log(step)) + 1);
  const beforeLimit = free > 0 ? (first * (step ** free - 1)) / (step - 1) : 0;
  const atLimit = (quantity - free) * limit;
  const lastUnit = free === quantity ? first * step ** (quantity - 1) : limit;
  return { gross: beforeLimit + atLimit, lastUnit };
}

export function quote(
  market: MarketState,
  good: TradeGoodId,
  side: TradeSide,
  quantity: number,
): Quote {
  if (!Number.isInteger(quantity) || quantity <= 0) {
    throw new RangeError(`quantity must be a positive integer, got ${quantity}`);
  }
  const { price } = market[good];
  const step = 1 + TRADE_GOODS[good].impact;
  const { min, max } = priceBounds(good);

  // Buying: units cost price, price·s, …; afterwards the price is one step up.
  // Selling: each unit fetches the price after it lands (price/s, price/s², …),
  // so buying q and selling q are exact mirrors: the price returns to where it
  // was and a round trip loses exactly the two fees.
  const { gross, lastUnit } =
    side === 'buy' ? walk(price, step, quantity, max) : walk(price / step, 1 / step, quantity, min);
  const priceAfter = side === 'buy' ? Math.min(lastUnit * step, max) : lastUnit;
  const fee = gross * MARKET.fee;
  return {
    side,
    good,
    quantity,
    gross: roundMoney(gross),
    fee: roundMoney(fee),
    total: roundMoney(side === 'buy' ? gross + fee : gross - fee),
    priceAfter,
  };
}

export function applyQuote(market: MarketState, q: Quote): MarketState {
  const current = market[q.good];
  return {
    ...market,
    [q.good]: {
      price: q.priceAfter,
      netSold: current.netSold + (q.side === 'sell' ? q.quantity : -q.quantity),
      cycleVolume: current.cycleVolume + q.quantity,
    },
  };
}

const REVERSION_PER_TICK = 2 ** (-1 / MARKET.reversionHalfLifeTicks);

/** Net units sold over the recent cycles (negative: net bought). */
export const windowNet = (good: MarketGood): number =>
  (good.netHistory ?? []).reduce((a, b) => a + b, 0);

/**
 * One tick of drift back to base, in log space (so ×2 and ÷2 decay alike).
 * Returns the same object when every price already sits at base, which is
 * most ticks of a quiet market.
 */
export function revertPrices(market: MarketState, tick = Infinity): MarketState {
  let next: Record<TradeGoodId, MarketGood> | null = null;
  for (const id of TRADE_GOOD_IDS) {
    const base = TRADE_GOODS[id].basePrice;
    const good = market[id];
    if (good.price === base || (good.holdUntil ?? -Infinity) > tick) continue;
    const logGap = Math.log(good.price / base) * REVERSION_PER_TICK;
    next ??= { ...market };
    next[id] = { ...good, price: Math.abs(logGap) < 1e-9 ? base : base * Math.exp(logGap) };
  }
  return next ?? market;
}

/** Closes a cycle on the exchange: volume restarts, net trade goes into the history. */
export function resetCycleVolume(market: MarketState): MarketState {
  const next = {} as Record<TradeGoodId, MarketGood>;
  for (const id of TRADE_GOOD_IDS) {
    const g = market[id];
    const net = g.netSold - (g.netAtCycleStart ?? 0);
    next[id] = {
      ...g,
      cycleVolume: 0,
      netAtCycleStart: g.netSold,
      netHistory: [...(g.netHistory ?? []), net].slice(-MARKET_EVENTS.windowCycles),
    };
  }
  return next;
}

/** Sets a good's price (kept within bounds), optionally holding it until a tick. */
export function shockPrice(
  market: MarketState,
  good: TradeGoodId,
  factor: number,
  holdUntil?: number,
): MarketState {
  const { min, max } = priceBounds(good);
  const price = Math.min(max, Math.max(min, market[good].price * factor));
  return {
    ...market,
    [good]: { ...market[good], price, ...(holdUntil !== undefined ? { holdUntil } : {}) },
  };
}
